import { type DistributiveOmit } from '@owl-nest/api-client/next'
import { type Address, type CreateAddress, AddressType } from '@owl-nest/api-client/latest'
//TODO: use @owl-nest/validator
import { phoneNumber } from './phone/phoneValidator'
import * as yup from 'yup'
import { t } from '@owl-nest/localize'
import { type Country } from '@owl-nest/config'

export type Model = DistributiveOmit<Address, 'id' | 'user_id' | 'has_shippings'> & { id: number | undefined }

export function empty(): Model {
  return {
    id: undefined,
    address1: '',
    address2: '',
    city: '',
    country: '',
    postal_code: '',
    state: '',
    phone_number: '',
    first_name: '',
    last_name: '',
    entity_name: '',
    type: AddressType.PERSONAL,
  }
}

export function format(model: CreateAddress | Model, countries: Country[]): string {
  const formatted: string[][] = []

  if (model.first_name) {
    if (formatted.length === 0) {
      formatted.push([])
    }
    formatted[0].push(model.first_name)
  }

  if (model.last_name) {
    if (formatted.length === 0) {
      formatted.push([])
    }
    formatted[0].push(model.last_name)
  }

  if (model.entity_name) {
    formatted.push([model.entity_name])
  }

  if (model.phone_number) {
    formatted.push([model.phone_number])
  }

  formatted.push([model.address1])

  if (model.address2) {
    formatted.push([model.address2])
  }

  formatted.push([model.postal_code, model.city])
  const country = countries.find((country) => country.code === model.country)
  formatted.push([country ? country.label() : model.country])

  return formatted.map((line) => line.join(' ')).join('\n')
}

export function formatMondialRelay(
  model: (CreateAddress | Model) & {
    delivery_type?: 'mondial_relay' | 'home_delivery'
    dropoff_id?: string
    dropoff_name?: string
  },
  countries: Country[],
): string {
  const formatted: string[][] = []

  if (model.dropoff_name) {
    if (formatted.length === 0) {
      formatted.push([])
    }
    formatted[0].push(model.dropoff_name)
  }

  formatted.push([model.address1])

  if (model.address2) {
    formatted.push([model.address2])
  }

  formatted.push([model.postal_code, model.city])
  const country = countries.find((country) => country.code === model.country)
  formatted.push([country ? country.label() : model.country])

  return formatted.map((line) => line.join(' ')).join('\n')
}

export function formatLine(model: CreateAddress | Model, countries: Country[]): string {
  const formatted: string[][] = []

  if (model.first_name) {
    if (formatted.length === 0) {
      formatted.push([])
    }
    formatted[0].push(model.first_name)
  }

  if (model.last_name) {
    if (formatted.length === 0) {
      formatted.push([])
    }
    formatted[0].push(model.last_name)
  }

  if (model.entity_name) {
    formatted.push([model.entity_name])
  }

  if (model.phone_number) {
    formatted.push([model.phone_number])
  }

  formatted.push([model.address1])

  if (model.address2) {
    formatted.push([model.address2])
  }

  formatted.push([model.postal_code, model.city])
  const country = countries.find((country) => country.code === model.country)
  formatted.push([country ? country.label() : model.country])

  return formatted.map((line) => line.join(' ')).join(', ')
}

export function baseSchema(phoneRequired: boolean): yup.ObjectSchema<any> {
  // TODO: set this in a common module
  yup.setLocale({
    mixed: {
      required: t('This field is required'),
      default: t('This field is invalid'),
    },
    string: {
      max: t('This field must contain a maximum of %(max)d characters').replace('%(max)d', '${max}'),
    },
  })

  return yup.object().shape({
    phone_number: phoneRequired ? yup.string().required().concat(phoneNumber()) : yup.mixed(),
    address1: yup.string().required().max(255),
    address2: yup.string().max(255),
    city: yup.string().required().max(140),
    country: yup.string().required(),
    postal_code: yup.string().required().max(144),
    state: yup.string().max(255),
  })
}

export function schema(phoneRequired: boolean): yup.ObjectSchema<any> {
  // TODO: set this in a common module
  yup.setLocale({
    mixed: {
      required: t('This field is required'),
      default: t('This field is invalid'),
    },
    string: {
      max: t('This field must contain a maximum of %(max)d characters').replace('%(max)d', '${max}'),
    },
  })

  return baseSchema(phoneRequired).shape({
    first_name: yup.string().when('type', {
      is: (value: any) => value === AddressType.PERSONAL,
      then: () => yup.string().max(30).required(),
      otherwise: () => yup.string().max(30),
    }),
    last_name: yup.string().when('type', {
      is: (value: any) => value === AddressType.PERSONAL,
      then: () => yup.string().max(30).required(),
      otherwise: () => yup.string().max(30),
    }),
    entity_name: yup.string().when('type', {
      is: (value: any) => value !== AddressType.PERSONAL,
      then: () => yup.string().max(250).required(),
      otherwise: () => yup.string().max(250),
    }),
    type: yup.mixed().oneOf(Object.values(AddressType), t('Address should have a type')),
  })
}

export function address(model: Model): CreateAddress {
  const safe = { ...model } as any
  delete safe.id
  delete safe.user_id
  delete safe.has_shippings
  return {
    ...safe,
    first_name: model.first_name || '',
    last_name: model.last_name || '',
    entity_name: model.entity_name || '',
  }
}
